import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import React from "react";
import { ui } from "../../interfaces/interfaces";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import useDebugInformation from "../../hooks/useDebugInformation";
import { handleBasicChange } from "@utils/general";
import { ContactPrefix, Prefix } from "@utils/BasicTypes";
import { Paper } from "@mui/material";
import { useAppSelector } from "@store/hooks";

type A11yProps = {
  (name: string, prefix: Prefix): {
    id: string
    name: string
    fullWidth: boolean
    autoComplete: string
    "aria-controls": string
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  }
}

export const a11yProps: A11yProps = (name, prefix) =>
({
  id: `input-${name.toLowerCase()}`,
  name,
  fullWidth: true,
  autoComplete: 'none',
  "aria-controls": `action-input-${name.toLowerCase()}`,
  onChange: (event) => handleBasicChange(event, prefix)
})

type Props = {
  addAddress: (type: string) => void
  removeAddress: () => void
  prefix: Exclude<ContactPrefix, "LandlordContact" | "CarePlanBillingContact"  | "CarePlanCustomerContact" | "CarePlanBillingContact" | "BookingPaymentContact" | "RelatedContact">
}

const handleChange = (event: React.ChangeEvent<HTMLInputElement>, memoProps: Props) => {
  event.target.value !== "other" && setApprovalNeeded(false)
  event.target.value === "other" && setApprovalNeeded(true)
  const isTenant = event.target.value === "tenant"
  const isOther = event.target.value === "other"
  ui.CustomerType = (event.target.value) as "tenant" | "landlord" | "home-owner" | "other"
  ui[`${memoProps.prefix}IsTenant`] = isTenant
  ui[`${memoProps.prefix}IsOther`] = isOther
};

const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>, memoProps: Props) => {
  const isTenant = event.target.name === "IsTenant" && event.target.checked
  event.target.name === "IsTenant" && setApprovalNeeded(isTenant)
}

const setApprovalNeeded = (value: boolean) => {
  ui.ApprovalNeeded = value
}

const handleRadioGroupChange = (event: React.ChangeEvent<HTMLInputElement>, memoProps: Props) => handleChange(event, memoProps)

const CustomerSwitch: React.FC<Props> = ({ addAddress, removeAddress, prefix }) => {
  const memoProps = React.useMemo(() => ({ addAddress, removeAddress, prefix }), [addAddress, prefix, removeAddress])
  const isTenant = useAppSelector(({ ui }) => ui[`${memoProps.prefix}IsTenant`])
  const isOther = useAppSelector(({ ui }) => ui[`${memoProps.prefix}IsOther`])
  const customerType = useAppSelector(({ ui }) => ui.CustomerType)
  const approvalNeeded = useAppSelector(({ ui }) => ui.ApprovalNeeded)

  React.useEffect(() => {
    if (customerType === "other") {
      addAddress("other")
    }
    else if ((customerType === "tenant" && approvalNeeded) || customerType === "landlord") {
      addAddress("landlord")
    }
    else {
      removeAddress()
    }
  }, [customerType, addAddress, removeAddress, isOther, approvalNeeded])

  useDebugInformation("CustomerSwitch", { memoProps, isTenant, customerType })
  return (
    memoProps.prefix === "PropertyContact" ? (
      <FormControl component="fieldset" sx={{ position: "relative", left: ".8rem", mt: 1 }} >
        <FormLabel component="legend" sx={{ fontSize: ".9rem", mb: 0 }}>Caller Type</FormLabel>
        <Paper elevation={1} >
          <RadioGroup row aria-label="customer-type" name="row-radio-buttons-group"
            value={customerType}
            onChange={(event) => handleRadioGroupChange(event, memoProps)} sx={{ height: "3rem", alignItems: "flex-start" }}>
            <FormControlLabel value="home-owner" control={<Radio />} label="H/Owner" sx={{ pl: 1, mb: 0 }} />
            <FormControlLabel value="landlord" control={<Radio />} label="Landlord" sx={{ pl: 1, mb: 0 }} />
            <FormControlLabel value="tenant" control={<Radio />} label="Tenant" sx={{ pl: 1, mb: 0 }} />
            <FormControlLabel value="other" control={<Radio />} label="Other" sx={{ pl: 1, mb: 0 }} />
            {(isTenant) && <FormControlLabel control={<Switch checked={approvalNeeded} name="IsTenant" onChange={(event) => handleSwitchChange(event, memoProps)} />} label="Approval Needed" />}
            {(isOther) && <FormControlLabel control={<Switch checked={true} name="IsOther" />} label="Approval Needed" />}
          </RadioGroup>
        </Paper>
      </FormControl>
    )
      : null
  )
}

export default React.memo(CustomerSwitch)